import { Navigate, RouteObject } from 'react-router-dom';
import { accountRoutes } from '@pages/Client/Account/routes';
import { calendarRoutes } from '@pages/Client/Calendar/routes';
import { dashboardRoutes } from '@pages/Client/Dashboard/routes';
import HealthReportLayout from '@pages/Client/HealthReport';
import { healthReportRoutes } from '@pages/Client/HealthReport/routes';
import { marketIntelligenceRoutes } from '@pages/Client/PricingStrategy/MarketIntelligence/routes';
import { occupancyRoutes } from '@pages/Client/PricingStrategy/OccupancyStrategy/routes';
import { SecureRoute } from '@pages/Client/components/SecureRoute';
import { surgeProtectionRoutes } from '@pages/Client/PricingStrategy/SurgeProtection/routes';
import { minStayRoutes } from '@pages/Client/PricingStrategy/MinStay/routes';
import { Feature } from '@common/store/features';
import { priceChartRoutes } from '@pages/Client/PriceChart/routes';

import CalendarLayout from '@pages/Client/Calendar';
import DashboardLayout from '@pages/Client/Dashboard';
import PriceChartLayout from '@pages/Client/PriceChart';
import AccountLayout from '@pages/Client/Account';
import FeaturesPage from '@pages/Client/Features';
import NotesPage from '@pages/Client/Notes';
import DealsPage from '@pages/Client/Deal';
import UsersLogPage from '@pages/Client/Logs/UsersLog';
import SettingsLogPage from '@pages/Client/Logs/SettingsLog';
import PricingSchedulePage from '@pages/Client/Logs/PricingSchedule';
import OutboundApiLogPage from '@pages/Client/Logs/OutboundApiLog';
import InboundApiLogPage from '@pages/Client/Logs/InboundApiLog';
import ReservationApiLogPage from '@pages/Client/Logs/ReservationApiLog';
import RequestIframeURLPage from '@pages/Client/RequestIframeURL';
import ChangePasswordPage from '@pages/Client/ChangePassword';
import DeletePropertyPage from '@pages/Client/DeleteProperty';
import RoomSetupPage from '@pages/Client/PricingStrategy/RoomSetup';
import DayOfTheWeekAdjustmentPage from '@pages/Client/PricingStrategy/DayOfTheWeekAdjustments';
import MonthlyAdjustmentPage from '@pages/Client/PricingStrategy/MonthlyAdjustments';
import HelpPage from '@pages/Client/Help';
import OccupancyStrategyLayout from '@pages/Client/PricingStrategy/OccupancyStrategy';
import After365DaysPage from '@pages/Client/PricingStrategy/After365Days';
import YieldingTagsPage from '@pages/Client/PricingStrategy/YieldingTags';
import LeadTimePage from '@pages/Client/PricingStrategy/LeadTime';
import BarLevelsPage from '@pages/Client/PricingStrategy/BarLevels';
import SegmentationPage from '@pages/Client/PricingStrategy/Segmentation';
import MarketIntelligenceLayout from '@pages/Client/PricingStrategy/MarketIntelligence';
import SurgeProtectionLayout from '@pages/Client/PricingStrategy/SurgeProtection';
import NoAccessPage from '@pages/Client/NoAccess';
import { AccessDeniedPage } from '@pages/Client/AccessDenied';
import Logout from '@pages/Client/Logout';
import MinStayPage from '@pages/Client/PricingStrategy/MinStay';
import { Error404 as Client404 } from '@pages/Client/Error/Error404';
import { ProtectedClientLayout } from '@pages/Client/ProtectedClientLayout';
import { HomePage } from '@pages/Client/Home';
import GroupDisplacement from '@pages/Client/PricingStrategy/GroupDisplacement';

export const clientRoutes: RouteObject[] = [
  {
    path: '/client',
    element: <ProtectedClientLayout />,
    children: [
      { index: true, element: <Navigate to="/client/404" replace /> }, // Avoids direct `/client` access
      { path: 'logout', element: <Logout /> },
      { path: '404', element: <Client404 /> },
      { path: '*', element: <Navigate to="/client/404" replace /> }
    ]
  },
  {
    path: '/client/:id',
    element: <ProtectedClientLayout />,
    children: [
      { index: true, element: <Navigate to="calendar" /> },
      { path: 'home', element: <SecureRoute children={<HomePage />} adminOnly /> },
      {
        path: 'calendar',
        element: <SecureRoute children={<CalendarLayout />} />,
        children: calendarRoutes
      },
      {
        path: 'dashboard',
        element: (
          <SecureRoute
            children={<DashboardLayout />}
            feature={[
              Feature.DashboardOnly,
              Feature.LimitedDashboard,
              Feature.ProfessionalDashboard,
              Feature.LimitedMarketInsights,
              Feature.ProMarketInsights
            ]}
          />
        ),
        children: dashboardRoutes
      },
      { path: 'features', element: <SecureRoute children={<FeaturesPage />} adminOnly /> },
      {
        path: 'chart',
        element: <SecureRoute children={<PriceChartLayout />} />,
        children: priceChartRoutes
      },
      { path: 'deal', element: <SecureRoute children={<DealsPage />} adminOnly /> },
      {
        path: 'customer-health',
        element: <SecureRoute children={<HealthReportLayout />} adminOnly />,
        children: healthReportRoutes
      },
      { path: 'notes', element: <SecureRoute children={<NotesPage />} adminOnly /> },
      { path: 'users-log', element: <SecureRoute children={<UsersLogPage />} adminOnly /> },
      { path: 'settings-log', element: <SecureRoute children={<SettingsLogPage />} adminOnly /> },
      { path: 'run-algo', element: <SecureRoute children={<PricingSchedulePage />} adminOnly /> },
      {
        path: 'outbound-api-log',
        element: <SecureRoute children={<OutboundApiLogPage />} adminOnly />
      },
      {
        path: 'inbound-api-log',
        element: <SecureRoute children={<InboundApiLogPage />} adminOnly />
      },
      {
        path: 'reservation-api-log',
        element: <SecureRoute children={<ReservationApiLogPage />} adminOnly />
      },
      {
        path: 'request-iframe-url',
        element: <SecureRoute children={<RequestIframeURLPage />} adminOnly />
      },
      {
        path: 'change-password',
        element: <SecureRoute children={<ChangePasswordPage />} adminOnly />
      },
      {
        path: 'delete-property',
        element: <SecureRoute children={<DeletePropertyPage />} adminOnly />
      },
      { path: 'help', element: <HelpPage /> },
      {
        path: 'day-of-the-week',
        element: <SecureRoute children={<DayOfTheWeekAdjustmentPage />} />
      },
      { path: 'monthly-settings', element: <SecureRoute children={<MonthlyAdjustmentPage />} /> },
      {
        path: 'account',
        element: <SecureRoute children={<AccountLayout />} />,
        children: accountRoutes
      },
      { path: 'room-setup', element: <SecureRoute children={<RoomSetupPage />} /> },
      {
        path: 'occupancy',
        element: <SecureRoute children={<OccupancyStrategyLayout />} />,
        children: occupancyRoutes
      },
      {
        path: 'after-year-setting',
        element: (
          <SecureRoute children={<After365DaysPage />} feature={Feature.EighteenMonthsPricing} />
        )
      },
      {
        path: 'group-displacement',
        element: <SecureRoute children={<GroupDisplacement />} />
      },
      {
        path: 'yielding-tags',
        element: <SecureRoute children={<YieldingTagsPage />} feature={Feature.YieldTag} />
      },
      {
        path: 'lead-time-adjustments',
        element: <SecureRoute children={<LeadTimePage />} feature={Feature.LeadTimeAdjustment} />
      },
      {
        path: 'surge-event',
        element: (
          <SecureRoute feature={Feature.SurgeProtection} children={<SurgeProtectionLayout />} />
        ),
        children: surgeProtectionRoutes
      },
      {
        path: 'min-stay',
        element: (
          <SecureRoute
            children={<MinStayPage />}
            feature={[Feature.MinimumStayRestrictions, Feature.LimitedMinimumStayRestrictions]}
          />
        ),
        children: minStayRoutes
      },
      {
        path: 'rate-settings',
        element: <SecureRoute children={<BarLevelsPage />} feature={Feature.BarLevels} />
      },
      {
        path: 'competitors',
        element: <SecureRoute children={<MarketIntelligenceLayout />} adminOnly />,
        children: marketIntelligenceRoutes
      },
      {
        path: 'segmentation',
        element: <SecureRoute children={<SegmentationPage />} feature={Feature.Segmentation} />
      },
      { path: 'no-access', element: <NoAccessPage /> },
      { path: 'access-denied', element: <AccessDeniedPage /> },
      { path: '*', element: <Navigate to="/client/404" replace /> }
    ]
  },
  {
    path: '*',
    element: <Navigate to="/client/404" replace />
  }
];
